import React, { useState, useEffect, useMemo } from 'react';

export default function PrintExportModal({
  isOpen,
  onClose,
  cardName = "Trophy Card",
  cardDimensions = { width: 400, height: 560 },
  orderNumber = "",
  coinCode = "",
  onGenerate,
  isProcessing = false,
  processingStep = "",
  error = "",
  success = false,
  onReset
}) {
  // Mode selection: 'book' (Score & Fold) or 'normal' (Pin alignment)
  const [printMode, setPrintMode] = useState('book');

  // Order & Coin references for print footer
  const [orderRef, setOrderRef] = useState(orderNumber);
  const [coinRef, setCoinRef] = useState(coinCode);

  useEffect(() => {
    if (orderNumber) setOrderRef(orderNumber);
  }, [orderNumber]);

  useEffect(() => {
    if (coinCode) setCoinRef(coinCode);
  }, [coinCode]);

  // Book mode options
  const [foldEdge, setFoldEdge] = useState('top'); // 'top' | 'bottom' | 'left' | 'right'
  const [foldGapMm, setFoldGapMm] = useState(1.5); // gap between cards along fold edge
  const [autoFlipBack, setAutoFlipBack] = useState(true); // smart auto-flip
  const [manualRotation, setManualRotation] = useState('auto'); // 'auto', 0, 90, 180, 270

  // Normal mode options
  const [normalArrangement, setNormalArrangement] = useState('stacked'); // 'stacked' | 'side_by_side'
  const [cardSpacingMm, setCardSpacingMm] = useState(15.0);
  const [includePinMarks, setIncludePinMarks] = useState(true);

  // Physical card sizing
  const [cardPreset, setCardPreset] = useState('poker'); // 'poker' | 'cr80' | 'mini' | 'custom'
  const [cardWidthMm, setCardWidthMm] = useState(63.5);
  const [cardHeightMm, setCardHeightMm] = useState(88.9);
  const [lockAspect, setLockAspect] = useState(true);

  // Common print options
  const [bleedMm, setBleedMm] = useState(2.0); // 0, 1, 2, 3 mm
  const [cropPaddingMm, setCropPaddingMm] = useState(3.5); // 1.5, 2.5, 3.5, 5.0, 6.5 mm
  const [dpi, setDpi] = useState(300); // 300 or 600
  const [pageOrientation, setPageOrientation] = useState('portrait'); // 'portrait' | 'landscape'
  const [centerOnPage, setCenterOnPage] = useState(true);
  const [offsetXmm, setOffsetXmm] = useState(0.0);
  const [offsetYmm, setOffsetYmm] = useState(0.0);

  // Additional useful print operations
  const [includeCropMarks, setIncludeCropMarks] = useState(true);
  const [includeFoldGuides, setIncludeFoldGuides] = useState(true);
  const [includeCornerGuides, setIncludeCornerGuides] = useState(false);
  const [includeColorBars, setIncludeColorBars] = useState(true);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [useOriginalAssets, setUseOriginalAssets] = useState(true);

  // Update card mm dimensions based on preset
  const handlePresetChange = (preset) => {
    setCardPreset(preset);
    if (preset === 'poker') {
      setCardWidthMm(63.5);
      setCardHeightMm(88.9);
    } else if (preset === 'cr80') {
      setCardWidthMm(85.6);
      setCardHeightMm(53.98);
    } else if (preset === 'mini') {
      setCardWidthMm(44.0);
      setCardHeightMm(67.0);
    }
  };

  // Card aspect ratio from designer
  const aspect = useMemo(() => {
    const w = cardDimensions.width || 400;
    const h = cardDimensions.height || 560;
    return w / h;
  }, [cardDimensions]);

  const handleWidthChange = (val) => {
    const num = Math.max(20, Math.min(250, parseFloat(val) || 20));
    setCardWidthMm(num);
    setCardPreset('custom');
    if (lockAspect && aspect) {
      setCardHeightMm(parseFloat((num / aspect).toFixed(1)));
    }
  };

  const handleHeightChange = (val) => {
    const num = Math.max(20, Math.min(250, parseFloat(val) || 20));
    setCardHeightMm(num);
    setCardPreset('custom');
    if (lockAspect && aspect) {
      setCardWidthMm(parseFloat((num * aspect).toFixed(1)));
    }
  };

  // Auto-switch orientation if card layout exceeds A4 width
  useEffect(() => {
    if (printMode === 'book') {
      if (foldEdge === 'left' || foldEdge === 'right') {
        const totalW = cardWidthMm * 2 + foldGapMm + bleedMm * 2;
        if (totalW > 170) {
          setPageOrientation('landscape');
        }
      } else {
        const totalH = cardHeightMm * 2 + foldGapMm + bleedMm * 2;
        if (totalH > 260) {
          setPageOrientation('portrait');
        }
      }
    } else {
      if (normalArrangement === 'side_by_side') {
        setPageOrientation('landscape');
      } else {
        setPageOrientation('portrait');
      }
    }
  }, [printMode, foldEdge, normalArrangement, cardWidthMm, cardHeightMm, foldGapMm, bleedMm]);

  // Compute effective back card rotation
  const effectiveBackRotation = useMemo(() => {
    if (manualRotation !== 'auto') {
      return parseInt(manualRotation, 10);
    }
    if (!autoFlipBack) return 0;
    if (foldEdge === 'top' || foldEdge === 'bottom') return 180;
    return 0;
  }, [manualRotation, autoFlipBack, foldEdge]);

  // Handle submit
  const handleExportClick = () => {
    const options = {
      card_name: cardName,
      order_number: orderRef || undefined,
      coin_code: coinRef || undefined,
      print_mode: printMode,
      fold_edge: foldEdge,
      fold_gap_mm: parseFloat(foldGapMm) || 1.5,
      auto_flip_back: autoFlipBack,
      back_rotation: effectiveBackRotation,
      card_width_mm: parseFloat(cardWidthMm),
      card_height_mm: parseFloat(cardHeightMm),
      bleed_mm: parseFloat(bleedMm),
      crop_padding_mm: parseFloat(cropPaddingMm) || 3.5,
      dpi: parseInt(dpi, 10),
      page_orientation: pageOrientation,
      normal_arrangement: normalArrangement,
      card_spacing_mm: parseFloat(cardSpacingMm) || 15.0,
      center_on_page: centerOnPage,
      offset_x_mm: parseFloat(offsetXmm) || 0.0,
      offset_y_mm: parseFloat(offsetYmm) || 0.0,
      include_pin_marks: includePinMarks,
      include_crop_marks: includeCropMarks,
      include_fold_guides: includeFoldGuides,
      include_corner_guides: includeCornerGuides,
      include_color_bars: includeColorBars,
      include_metadata: includeMetadata,
      use_original_assets: useOriginalAssets,
      card_width: cardDimensions.width || 400,
      card_height: cardDimensions.height || 560
    };
    if (onGenerate) {
      onGenerate(options);
    }
  };

  // Listen for Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !isProcessing && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProcessing, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="print-export-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing && onClose) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(5px)',
        zIndex: 10000000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div
        style={{
          width: '920px',
          maxWidth: '96vw',
          maxHeight: '92vh',
          background: 'var(--card-bg, #18181b)',
          border: '1px solid var(--border-color, #3f3f46)',
          borderRadius: '14px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          overflow: 'hidden',
          color: 'var(--text-main, #f4f4f5)',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 22px',
            borderBottom: '1px solid var(--border-color, #27272a)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--panel-bg, #1f1f23)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '22px' }}>🖨️</span>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
                  Export High-Res Print (Epson EcoTank L8160)
                </h3>
                <span
                  style={{
                    backgroundColor: '#10b98122',
                    color: '#34d399',
                    border: '1px solid #059669',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}
                >
                  A4 Photo Print (210 × 297 mm)
                </span>
                <span
                  style={{
                    backgroundColor: '#3b82f622',
                    color: '#60a5fa',
                    border: '1px solid #2563eb',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}
                >
                  ✨ Original Assets Used
                </span>
              </div>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-mute, #a1a1aa)' }}>
                Generate a precision PDF with crop marks, pin registration, and folding options for "{cardName}".
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              fontSize: '20px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px 8px',
              borderRadius: '6px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        {isProcessing ? (
          /* Moving / Animated Processing Graphic */
          <div
            data-testid="print-export-processing"
            style={{
              padding: '60px 40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              minHeight: '380px',
              background: 'radial-gradient(circle at center, #1f2937 0%, #111827 100%)'
            }}
          >
            {/* Animated Printer Mechanism */}
            <div
              style={{
                position: 'relative',
                width: '180px',
                height: '110px',
                backgroundColor: '#1f2937',
                border: '2px solid #3b82f6',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 0 30px rgba(59, 130, 246, 0.35)',
                overflow: 'hidden'
              }}
            >
              {/* Paper feed coming out */}
              <div
                style={{
                  width: '110px',
                  height: '70px',
                  backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  position: 'absolute',
                  top: '-15px',
                  animation: 'printPaperSlide 2s infinite ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '4px'
                }}
              >
                <div style={{ width: '80%', height: '4px', backgroundColor: '#3b82f6', borderRadius: '2px', marginBottom: '3px' }} />
                <div style={{ width: '60%', height: '3px', backgroundColor: '#e2e8f0', borderRadius: '2px', marginBottom: '2px' }} />
                <div style={{ width: '70%', height: '3px', backgroundColor: '#e2e8f0', borderRadius: '2px' }} />
              </div>

              {/* Moving Printhead Beam */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '22px',
                  left: 0,
                  right: 0,
                  height: '8px',
                  backgroundColor: '#0f172a',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '14px',
                    backgroundColor: '#10b981',
                    borderRadius: '3px',
                    boxShadow: '0 0 12px #10b981',
                    animation: 'printHeadSweep 1.2s infinite alternate ease-in-out'
                  }}
                />
              </div>

              {/* Status LED */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '6px',
                  right: '12px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  boxShadow: '0 0 8px #10b981',
                  animation: 'pulseGlow 0.8s infinite alternate'
                }}
              />
              <span style={{ position: 'absolute', bottom: '5px', left: '12px', fontSize: '9px', color: '#94a3b8', fontWeight: '600' }}>
                EPSON L8160 MASTER
              </span>
            </div>

            {/* Step & Description */}
            <div style={{ textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '700', color: '#60a5fa' }}>
                Rendering Print File...
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1' }}>
                {processingStep || "Resolving uncompressed master assets & rasterizing at 300 DPI..."}
              </p>
            </div>

            {/* Animation CSS Injected */}
            <style>{`
              @keyframes printHeadSweep {
                0% { transform: translateX(10px); }
                100% { transform: translateX(138px); }
              }
              @keyframes printPaperSlide {
                0% { transform: translateY(-5px); opacity: 0.9; }
                50% { transform: translateY(12px); opacity: 1; }
                100% { transform: translateY(-5px); opacity: 0.9; }
              }
              @keyframes pulseGlow {
                0% { opacity: 0.4; }
                100% { opacity: 1; }
              }
            `}</style>
          </div>
        ) : success ? (
          /* Success Screen */
          <div
            data-testid="print-export-success"
            style={{
              padding: '50px 30px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#10b98122',
                border: '2px solid #10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}
            >
              ✅
            </div>
            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#ffffff' }}>
              Print PDF Ready & Downloaded!
            </h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', maxWidth: '480px', lineHeight: 1.5 }}>
              Your file is calibrated for an <strong>Epson EcoTank L8160</strong> A4 sheet. Print using <strong>Actual Size (100% scale)</strong> and borderless/standard photo settings.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button
                onClick={handleExportClick}
                style={{
                  backgroundColor: '#27272a',
                  color: '#ffffff',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '9px 18px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Download Again
              </button>
              <button
                onClick={onClose}
                style={{
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '9px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Main Configuration View */
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Left Options Column */}
            <div
              style={{
                flex: '1.2',
                overflowY: 'auto',
                padding: '18px 22px',
                borderRight: '1px solid var(--border-color, #27272a)',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px'
              }}
            >
              {error && (
                <div
                  style={{
                    backgroundColor: '#ef444422',
                    border: '1px solid #ef4444',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#f87171',
                    fontSize: '12px'
                  }}
                >
                  ⚠️ {error}
                </div>
              )}

              {/* Mode Selector Tabs */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>
                  Print & Alignment Mode
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    type="button"
                    data-testid="mode-book-btn"
                    onClick={() => setPrintMode('book')}
                    style={{
                      backgroundColor: printMode === 'book' ? '#2563eb22' : '#27272a',
                      border: `1.5px solid ${printMode === 'book' ? '#3b82f6' : '#3f3f46'}`,
                      borderRadius: '8px',
                      padding: '10px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: printMode === 'book' ? '#ffffff' : '#a1a1aa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '13px' }}>
                      <span>📖</span>
                      <span>Book Mode (Score & Fold)</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                      Cards joined along fold edge. Score and fold sheet in half.
                    </div>
                  </button>

                  <button
                    type="button"
                    data-testid="mode-normal-btn"
                    onClick={() => setPrintMode('normal')}
                    style={{
                      backgroundColor: printMode === 'normal' ? '#2563eb22' : '#27272a',
                      border: `1.5px solid ${printMode === 'normal' ? '#3b82f6' : '#3f3f46'}`,
                      borderRadius: '8px',
                      padding: '10px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: printMode === 'normal' ? '#ffffff' : '#a1a1aa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', fontSize: '13px' }}>
                      <span>📍</span>
                      <span>Normal Mode (Pin Alignment)</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                      Separate cards with precision registration crosshairs for pin method.
                    </div>
                  </button>
                </div>
              </div>

              {/* Mode Specific Settings */}
              {printMode === 'book' ? (
                <div style={{ backgroundColor: '#1f1f23', border: '1px solid #2e2e33', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚙️</span>
                    <span>Book Fold Controls</span>
                  </div>

                  {/* Fold Edge */}
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                      Fold Edge (Where cards join):
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                      {[
                        { id: 'top', label: 'Top ⬆️' },
                        { id: 'bottom', label: 'Bottom ⬇️' },
                        { id: 'left', label: 'Left ⬅️' },
                        { id: 'right', label: 'Right ➡️' }
                      ].map(edge => (
                        <button
                          key={edge.id}
                          type="button"
                          data-testid={`fold-edge-${edge.id}`}
                          onClick={() => setFoldEdge(edge.id)}
                          style={{
                            backgroundColor: foldEdge === edge.id ? '#3b82f6' : '#27272a',
                            color: foldEdge === edge.id ? '#ffffff' : '#d4d4d8',
                            border: '1px solid #3f3f46',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {edge.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Fold Gap */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: '600' }}>
                        Fold Gap / Spine Clearance (mm):
                      </label>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8' }}>
                        {foldGapMm} mm
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="range"
                        min="0.0"
                        max="8.0"
                        step="0.5"
                        value={foldGapMm}
                        onChange={(e) => setFoldGapMm(parseFloat(e.target.value))}
                        style={{ flex: 1, cursor: 'pointer' }}
                      />
                      <input
                        type="number"
                        min="0.0"
                        max="15.0"
                        step="0.5"
                        value={foldGapMm}
                        onChange={(e) => setFoldGapMm(parseFloat(e.target.value) || 0)}
                        style={{
                          width: '60px',
                          backgroundColor: '#27272a',
                          border: '1px solid #3f3f46',
                          borderRadius: '4px',
                          color: '#ffffff',
                          padding: '4px 6px',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                      Spacing to account for cardstock fold crease / spine thickness.
                    </span>
                  </div>

                  {/* Smart Auto-Flip */}
                  <div style={{ borderTop: '1px solid #2e2e33', paddingTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#ffffff' }}>
                          <input
                            type="checkbox"
                            checked={autoFlipBack}
                            onChange={(e) => setAutoFlipBack(e.target.checked)}
                          />
                          <span>Smart Auto-Flip (Back Card Rotation)</span>
                        </label>
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '20px' }}>
                          {foldEdge === 'top' || foldEdge === 'bottom'
                            ? '🔄 Inverts back 180° so it folds right-side up.'
                            : '📖 Keeps back upright (0°) for side book fold.'}
                        </div>
                      </div>
                      <select
                        value={manualRotation}
                        onChange={(e) => setManualRotation(e.target.value)}
                        style={{
                          backgroundColor: '#27272a',
                          border: '1px solid #3f3f46',
                          borderRadius: '4px',
                          color: '#ffffff',
                          padding: '4px 8px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                      >
                        <option value="auto">Auto ({effectiveBackRotation}°)</option>
                        <option value="0">Force 0° (Normal)</option>
                        <option value="90">Force 90° (Clockwise)</option>
                        <option value="180">Force 180° (Upside down)</option>
                        <option value="270">Force 270° (Counter)</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ backgroundColor: '#1f1f23', border: '1px solid #2e2e33', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚙️</span>
                    <span>Normal Mode Positioning & Pin Alignment</span>
                  </div>

                  {/* Arrangement */}
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                      Sheet Arrangement:
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setNormalArrangement('stacked')}
                        style={{
                          backgroundColor: normalArrangement === 'stacked' ? '#3b82f6' : '#27272a',
                          color: '#ffffff',
                          border: '1px solid #3f3f46',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Stacked (Vertical)
                      </button>
                      <button
                        type="button"
                        onClick={() => setNormalArrangement('side_by_side')}
                        style={{
                          backgroundColor: normalArrangement === 'side_by_side' ? '#3b82f6' : '#27272a',
                          color: '#ffffff',
                          border: '1px solid #3f3f46',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Side by Side (Horizontal)
                      </button>
                    </div>
                  </div>

                  {/* Card Spacing */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: '600' }}>
                        Distance Between Cards:
                      </label>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8' }}>
                        {cardSpacingMm} mm
                      </span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="40"
                      step="1"
                      value={cardSpacingMm}
                      onChange={(e) => setCardSpacingMm(parseFloat(e.target.value))}
                      style={{ width: '100%', cursor: 'pointer' }}
                    />
                  </div>

                  {/* Pin Registration Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: '#f4f4f5' }}>
                    <input
                      type="checkbox"
                      checked={includePinMarks}
                      onChange={(e) => setIncludePinMarks(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Include Pin Registration Targets (⊕ PIN A, B, C, D)</span>
                  </label>
                  <span style={{ fontSize: '10px', color: '#94a3b8', margin: '-6px 0 0 22px' }}>
                    Enables the pin method: pierce pins through matching crosshairs to guarantee 100% front/back register.
                  </span>

                  {/* Centering and Offsets */}
                  <div style={{ borderTop: '1px solid #2e2e33', paddingTop: '8px', marginTop: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: '#f4f4f5' }}>
                      <input
                        type="checkbox"
                        checked={centerOnPage}
                        onChange={(e) => setCenterOnPage(e.target.checked)}
                      />
                      <span>Center Cards on A4 Page</span>
                    </label>
                    {!centerOnPage && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '6px', marginLeft: '22px' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Offset X (mm)</span>
                          <input
                            type="number"
                            value={offsetXmm}
                            onChange={(e) => setOffsetXmm(parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', backgroundColor: '#27272a', border: '1px solid #3f3f46', borderRadius: '4px', color: '#ffffff', padding: '4px 6px', fontSize: '11px' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Offset Y (mm)</span>
                          <input
                            type="number"
                            value={offsetYmm}
                            onChange={(e) => setOffsetYmm(parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', backgroundColor: '#27272a', border: '1px solid #3f3f46', borderRadius: '4px', color: '#ffffff', padding: '4px 6px', fontSize: '11px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Physical Card Dimensions */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>
                  Target Physical Card Size
                </label>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  {[
                    { id: 'poker', label: 'Poker / Trading (63.5 × 88.9 mm)' },
                    { id: 'cr80', label: 'CR80 ID (85.6 × 54 mm)' },
                    { id: 'custom', label: 'Custom' }
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handlePresetChange(p.id)}
                      style={{
                        backgroundColor: cardPreset === p.id ? '#2563eb' : '#27272a',
                        color: cardPreset === p.id ? '#ffffff' : '#d4d4d8',
                        border: '1px solid #3f3f46',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Width (mm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={cardWidthMm}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      style={{
                        width: '100%',
                        backgroundColor: '#27272a',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '6px 8px',
                        fontSize: '12px'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setLockAspect(!lockAspect)}
                    title={lockAspect ? "Aspect ratio locked" : "Aspect ratio unlocked"}
                    style={{
                      marginTop: '14px',
                      background: 'none',
                      border: 'none',
                      color: lockAspect ? '#60a5fa' : '#71717a',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    {lockAspect ? '🔗' : '🔓'}
                  </button>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Height (mm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={cardHeightMm}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      style={{
                        width: '100%',
                        backgroundColor: '#27272a',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '6px 8px',
                        fontSize: '12px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Quality & Additional Print Options */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>
                  Print Calibration & Operations
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Bleed Margin</span>
                    <select
                      data-testid="bleed-margin-select"
                      value={bleedMm}
                      onChange={(e) => setBleedMm(parseFloat(e.target.value))}
                      style={{
                        width: '100%',
                        backgroundColor: '#27272a',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '6px 8px',
                        fontSize: '12px'
                      }}
                    >
                      <option value="0">0 mm (Clean Card Edge)</option>
                      <option value="1">1.0 mm (Tight)</option>
                      <option value="2">2.0 mm (Standard Photo)</option>
                      <option value="3">3.0 mm (Heavy Stock)</option>
                    </select>
                  </div>

                  <div>
                    <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Crop Line Padding</span>
                    <select
                      data-testid="crop-padding-select"
                      value={cropPaddingMm}
                      onChange={(e) => setCropPaddingMm(parseFloat(e.target.value))}
                      style={{
                        width: '100%',
                        backgroundColor: '#27272a',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '6px 8px',
                        fontSize: '12px'
                      }}
                    >
                      <option value="1.5">1.5 mm (Tight)</option>
                      <option value="2.5">2.5 mm (Compact)</option>
                      <option value="3.5">3.5 mm (Standard - Recommended)</option>
                      <option value="5.0">5.0 mm (Comfortable)</option>
                      <option value="6.5">6.5 mm (Wide Clearance)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '8px' }}>
                  <span style={{ fontSize: '10px', color: '#a1a1aa' }}>Print Resolution</span>
                  <select
                    value={dpi}
                    onChange={(e) => setDpi(parseInt(e.target.value, 10))}
                    style={{
                      width: '100%',
                      backgroundColor: '#27272a',
                      border: '1px solid #3f3f46',
                      borderRadius: '6px',
                      color: '#ffffff',
                      padding: '6px 8px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="300">300 DPI (High Quality Photo)</option>
                    <option value="600">600 DPI (Ultra Fine Detail)</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeCropMarks}
                      onChange={(e) => setIncludeCropMarks(e.target.checked)}
                    />
                    <span>Corner Crop Lines</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeFoldGuides}
                      onChange={(e) => setIncludeFoldGuides(e.target.checked)}
                    />
                    <span>Fold & Score Lines</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeCornerGuides}
                      onChange={(e) => setIncludeCornerGuides(e.target.checked)}
                    />
                    <span>Corner Punch Guides (3mm)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeColorBars}
                      onChange={(e) => setIncludeColorBars(e.target.checked)}
                    />
                    <span>Color Calibration Bar</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      data-testid="include-metadata-checkbox"
                      checked={includeMetadata}
                      onChange={(e) => setIncludeMetadata(e.target.checked)}
                    />
                    <span>Order & Job Details Footer</span>
                  </label>
                  {includeMetadata && (
                    <div style={{ marginTop: '4px', marginBottom: '6px', paddingLeft: '18px', display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        data-testid="print-order-number-input"
                        placeholder="Order # (e.g. 1042)"
                        value={orderRef}
                        onChange={(e) => setOrderRef(e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: '#27272a',
                          border: '1px solid #3f3f46',
                          borderRadius: '4px',
                          color: '#f4f4f5',
                          padding: '4px 8px',
                          fontSize: '11px'
                        }}
                      />
                      <input
                        type="text"
                        data-testid="print-coin-code-input"
                        placeholder="Coin Code (e.g. TC-402)"
                        value={coinRef}
                        onChange={(e) => setCoinRef(e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: '#27272a',
                          border: '1px solid #3f3f46',
                          borderRadius: '4px',
                          color: '#f4f4f5',
                          padding: '4px 8px',
                          fontSize: '11px'
                        }}
                      />
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#d4d4d8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useOriginalAssets}
                      onChange={(e) => setUseOriginalAssets(e.target.checked)}
                    />
                    <span style={{ color: '#34d399', fontWeight: '600' }}>Master Uncompressed Assets</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Right Live Layout Graphic Column */}
            <div
              style={{
                flex: '1',
                padding: '20px',
                backgroundColor: '#121214',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '12px' }}>
                Live A4 Sheet Preview ({pageOrientation})
              </div>

              {/* A4 Sheet Miniature Representation */}
              <div
                data-testid="print-sheet-preview"
                style={{
                  width: pageOrientation === 'portrait' ? '210px' : '280px',
                  height: pageOrientation === 'portrait' ? '297px' : '210px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
                  borderRadius: '3px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden'
                }}
              >
                {/* Page Margin Guide (Subtle) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '6px',
                    left: '6px',
                    right: '6px',
                    bottom: '6px',
                    border: '1px dashed #e2e8f0',
                    pointerEvents: 'none'
                  }}
                />

                {/* Render Simulated Cards */}
                {printMode === 'book' ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: foldEdge === 'top' || foldEdge === 'bottom' ? 'column' : 'row',
                      alignItems: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Simulated Card 1 */}
                    <div
                      style={{
                        width: `${Math.round(cardWidthMm * 0.9)}px`,
                        height: `${Math.round(cardHeightMm * 0.9)}px`,
                        backgroundColor: (foldEdge === 'top' || foldEdge === 'left') ? '#b91c1c' : '#1e3a8a',
                        border: '1px solid #000000',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: '700',
                        transform: (foldEdge === 'top' && effectiveBackRotation === 180) ? 'rotate(180deg)' : 'none'
                      }}
                    >
                      {(foldEdge === 'top' || foldEdge === 'left') ? 'BACK 🔄' : 'FRONT 👤'}
                    </div>

                    {/* Fold Crease / Gap Line */}
                    <div
                      style={{
                        width: foldEdge === 'top' || foldEdge === 'bottom' ? '120%' : `${Math.max(2, Math.round(foldGapMm * 0.9))}px`,
                        height: foldEdge === 'top' || foldEdge === 'bottom' ? `${Math.max(2, Math.round(foldGapMm * 0.9))}px` : '120%',
                        backgroundColor: '#f59e0b',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '1px',
                          borderTop: '1px dashed #d97706'
                        }}
                      />
                    </div>

                    {/* Simulated Card 2 */}
                    <div
                      style={{
                        width: `${Math.round(cardWidthMm * 0.9)}px`,
                        height: `${Math.round(cardHeightMm * 0.9)}px`,
                        backgroundColor: (foldEdge === 'top' || foldEdge === 'left') ? '#1e3a8a' : '#b91c1c',
                        border: '1px solid #000000',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: '700',
                        transform: (foldEdge === 'bottom' && effectiveBackRotation === 180) ? 'rotate(180deg)' : 'none'
                      }}
                    >
                      {(foldEdge === 'top' || foldEdge === 'left') ? 'FRONT 👤' : 'BACK 🔄'}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: normalArrangement === 'stacked' ? 'column' : 'row',
                      gap: `${Math.round(cardSpacingMm * 0.5)}px`,
                      alignItems: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Front Card */}
                    <div
                      style={{
                        width: `${Math.round(cardWidthMm * 0.75)}px`,
                        height: `${Math.round(cardHeightMm * 0.75)}px`,
                        backgroundColor: '#1e3a8a',
                        border: '1px solid #000000',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '8px',
                        fontWeight: '700',
                        position: 'relative'
                      }}
                    >
                      FRONT
                      {includePinMarks && (
                        <>
                          <span style={{ position: 'absolute', top: '-8px', left: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', top: '-8px', right: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', bottom: '-8px', left: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', bottom: '-8px', right: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                        </>
                      )}
                    </div>

                    {/* Back Card */}
                    <div
                      style={{
                        width: `${Math.round(cardWidthMm * 0.75)}px`,
                        height: `${Math.round(cardHeightMm * 0.75)}px`,
                        backgroundColor: '#b91c1c',
                        border: '1px solid #000000',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '8px',
                        fontWeight: '700',
                        position: 'relative'
                      }}
                    >
                      BACK
                      {includePinMarks && (
                        <>
                          <span style={{ position: 'absolute', top: '-8px', left: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', top: '-8px', right: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', bottom: '-8px', left: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                          <span style={{ position: 'absolute', bottom: '-8px', right: '-8px', fontSize: '7px', color: '#000' }}>⊕</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Color Calibration Strip in Preview */}
                {includeColorBars && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '12px',
                      display: 'flex',
                      gap: '2px'
                    }}
                  >
                    {['#00ffff', '#ff00ff', '#ffff00', '#000000', '#ff0000', '#00ff00', '#0000ff'].map(c => (
                      <div key={c} style={{ width: '4px', height: '4px', backgroundColor: c }} />
                    ))}
                  </div>
                )}
              </div>

              {/* Legend & Details */}
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#94a3b8', textAlign: 'center', lineHeight: 1.4 }}>
                <div>A4 Sheet: 210 × 297 mm</div>
                <div>Card: {cardWidthMm} × {cardHeightMm} mm • Bleed: {bleedMm} mm • Crop Gap: {cropPaddingMm} mm</div>
                <div style={{ color: '#38bdf8' }}>Target: Epson EcoTank L8160 ({dpi} DPI)</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Buttons */}
        {!isProcessing && !success && (
          <div
            style={{
              padding: '14px 22px',
              borderTop: '1px solid var(--border-color, #27272a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--panel-bg, #1f1f23)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
              <span>📄</span>
              <span>Generates Adobe / Epson print-ready PDF container</span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-mute, #d4d4d8)',
                  border: '1px solid var(--border-color, #3f3f46)',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="print-export-submit-btn"
                onClick={handleExportClick}
                style={{
                  background: 'var(--primary-gradient, var(--primary-color, #2563eb))',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)'
                }}
              >
                <span>🖨️</span>
                <span>Export Print PDF</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
